import React, { useEffect, useState } from 'react';
import { ExternalLink, Globe } from 'lucide-react';
import InviteEmbed, { parseInviteCode } from './InviteEmbed';

interface LinkMetadata {
  title?: string;
  description?: string;
  image?: { url: string };
  url?: string;
  publisher?: string;
}

interface LinkEmbedProps {
  url: string;
}

export function LinkEmbed({ url }: LinkEmbedProps) {
  const [metadata, setMetadata] = useState<LinkMetadata | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // A PsydeChat invite link gets its own rich card (server icon, description,
  // member count, JOIN button) instead of the generic Microlink preview —
  // which for an invite URL just showed the site's own title/description and
  // gave the reader nothing to act on. Intercepting here covers every link
  // surface at once: FeedPostCard and NeuralMessageBubble both route all
  // URLs through this component.
  const inviteCode = parseInviteCode(url);

  useEffect(() => {
    let isMounted = true;

    // Skip the metadata fetch entirely for invites — InviteEmbed resolves
    // the server itself via the invite_preview RPC.
    if (inviteCode) {
      setLoading(false);
      return;
    }

    const fetchMetadata = async () => {
      try {
        setLoading(true);
        // Using Microlink API to fetch URL metadata
        const response = await fetch(`https://api.microlink.io/?url=${encodeURIComponent(url)}`);
        
        if (!response.ok) throw new Error('Network response was not ok');
        
        const data = await response.json() as any;
        
        if (isMounted && data.status === 'success') {
          setMetadata(data.data);
        } else {
          throw new Error('Failed to parse metadata');
        }
      } catch (err) {
        if (isMounted) setError(true);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchMetadata();

    return () => {
      isMounted = false;
    };
  }, [url, inviteCode]);

  if (inviteCode) return <InviteEmbed code={inviteCode} />;

  if (error || (!loading && !metadata?.title)) return null;

  if (loading) {
    return (
      <div className="mt-2 p-4 rounded-xl bg-white/5 border border-white/10 max-w-[450px] flex gap-4 animate-pulse">
        <div className="w-16 h-16 bg-white/10 rounded-lg shrink-0" />
        <div className="flex-1 space-y-2 py-1">
          <div className="h-3 bg-white/10 rounded w-3/4" />
          <div className="h-2 bg-white/10 rounded w-full" />
          <div className="h-2 bg-white/10 rounded w-5/6" />
        </div>
      </div>
    );
  }

  return (
    <a 
      href={metadata!.url || url} 
      target="_blank" 
      rel="noopener noreferrer"
      className="mt-2 block w-full max-w-2xl rounded-r-lg rounded-l-md bg-[#161224]/80 border-l-[4px] border-l-[var(--neon)] hover:bg-[#1e1933]/90 transition-all overflow-hidden group shadow-md"
    >
      <div className="p-4 flex flex-col">
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="flex items-center gap-1.5 mb-1.5">
            <span className="text-xs font-semibold text-white/60 truncate">
              {metadata!.publisher || new URL(url).hostname}
            </span>
          </div>
          
          <h4 className="text-base font-bold text-[var(--neon)] mb-2 leading-snug group-hover:underline">
            {metadata!.title}
          </h4>
          
          {metadata!.description && (
            <p className="text-sm text-white/80 whitespace-pre-wrap leading-relaxed mb-4">
              {metadata!.description}
            </p>
          )}
        </div>
        
        {metadata!.image?.url && (
          <div className="w-full max-w-[400px] rounded-lg overflow-hidden border border-white/10 bg-black/40 mt-1">
            <img 
              src={metadata!.image.url} 
              alt={metadata!.title} 
              className="w-full h-auto object-contain max-h-[300px]"
            />
          </div>
        )}
      </div>
    </a>
  );
}
